import { useState } from 'react';
import toast from 'react-hot-toast';
import { useApiCache } from '../../context/api-context';
import { useApp as portalApp } from '../../context/usePortal';
import { ayeApi } from '../../core';
import type { UserI } from '../../types/user';
import { Button } from '../ui/button';
import EmailOtpVerificationDialog from './EmailOtpVerificationDialog';

export const UserProfile = () => {
  const { saveUser, user } = portalApp();
  const { dispatch } = useApiCache();
  const [dataChanged, setDataChanged] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [userData, setUserData] = useState<Partial<UserI> | null | undefined>(
    user
  );
  const [emailError, setEmailError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Email OTP flow state
  const [emailOtpStep, setEmailOtpStep] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');
  const [otpError, setOtpError] = useState<{
    message: string;
    code: string;
  } | null>(null);
  const [isOtpLoading, setIsOtpLoading] = useState(false);

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0];
  };

  const validateEmail = (email: string) => {
    if (!email || email.trim().length === 0) {
      return false;
    }
    // Basic email validation regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDataChanged(true);
    const { name, value } = e.target;
    setUserData((prevData) => ({ ...prevData, [name]: value }));

    if (name === 'user_email') {
      if (!value || value.trim().length === 0) {
        setEmailError('Email is required');
      } else if (!validateEmail(value)) {
        setEmailError('Please enter a valid email address');
      } else {
        setEmailError(null);
      }
    }

    if (name === 'user_name') {
      if (!value || value.trim().length === 0) {
        setNameError('Name is required');
      } else {
        setNameError(null);
      }
    }
  };

  const hasNameChanged = () => {
    if (!userData || !user) return false;
    return (user.user_name ?? '') !== (userData.user_name ?? '');
  };

  const hasEmailChanged = () => {
    if (!userData || !user) return false;
    return (user.user_email ?? '') !== (userData.user_email ?? '');
  };

  const saveProfile = async (emailToSave?: string) => {
    if (!userData) return;

    setIsLoading(true);
    const payload = {
      user_name: userData.user_name ?? '',
      user_email: emailToSave ?? userData.user_email ?? '',
      user_birthday: userData.user_birthday
        ? new Date(userData.user_birthday).toISOString()
        : '',
      text_message: user?.text_message ?? false,
      marketing_and_offers: user?.marketing_and_offers ?? false,
      newsletter: user?.newsletter ?? false
    };
    const response = await saveUser(payload);
    setIsLoading(false);

    if (response?.isOk() && response.response?.success) {
      setDataChanged(false);

      // Clear payment methods cache if name changed
      if (hasNameChanged()) {
        dispatch({
          type: 'SET_DATA',
          key: 'payment_consents',
          data: { payment_consents: [] }
        });
      }

      toast.success('Profile updated successfully.');
    } else toast.error('Some error occurred');
  };

  const handleSubmit = () => {
    if (hasEmailChanged()) {
      // Email has changed — show confirm modal first (warns about payment consent revocation)
      setShowConfirmModal(true);
      return;
    }

    if (hasNameChanged()) {
      setShowConfirmModal(true);
      return;
    }

    void saveProfile();
  };

  // Called after user confirms the warning modal
  const handleConfirmedSave = () => {
    setShowConfirmModal(false);

    if (hasEmailChanged()) {
      // Open OTP dialog — it sends the OTP itself
      const newEmail = userData?.user_email ?? '';
      setPendingEmail(newEmail);
      setOtpError(null);
      setEmailOtpStep(true);
      return;
    }

    void saveProfile();
  };

  const handleOtpSubmit = async (otp: string) => {
    setIsOtpLoading(true);
    const res = await ayeApi.updateEmail({
      body: { email: pendingEmail, otp }
    });
    setIsOtpLoading(false);

    if (!res.success) {
      setOtpError({ message: res.error.message, code: res.error.code });
      return;
    }

    // Email updated — clear payment consent cache, save remaining fields
    dispatch({
      type: 'SET_DATA',
      key: 'payment_consents',
      data: { payment_consents: [] }
    });

    setEmailOtpStep(false);
    // Save remaining fields (name, birthday, preferences) — pass verified email
    await saveProfile(pendingEmail);
  };

  // ── OTP step (modal) ─────────────────────────────────────────────────────

  return (
    <form>
      <div className="px-4 py-6 sm:p-6 lg:pb-8">
        <div>
          <h2 className="text-lg font-medium leading-6 text-gray-900">
            Profile
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Some of this information will be displayed on AyeFace App when you
            use it, so be careful what you share.
          </p>
        </div>

        <div className="mt-6 flex flex-col lg:flex-row">
          <div className="flex-grow space-y-6">
            <div className="col-span-12 sm:col-span-6">
              <label
                htmlFor="name"
                className="block text-sm font-medium text-gray-700"
              >
                Your name
              </label>
              <input
                type="text"
                name="user_name"
                id="name"
                value={userData?.user_name ?? ''}
                onChange={handleInputChange}
                placeholder={user?.user_name ?? 'Please add your name'}
                autoComplete="given-name"
                className={`mt-1 block w-64 rounded-md border ${
                  nameError ? 'border-red-500' : 'border-gray-300'
                } px-3 py-2 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-sky-500 sm:text-sm`}
              />
              {nameError && (
                <p className="mt-1 text-xs text-red-500">{nameError}</p>
              )}
            </div>
          </div>
        </div>

        <div className="mt-2 grid grid-cols-12 gap-6">
          <div className="col-span-12 w-64 sm:col-span-6">
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700"
            >
              Email
            </label>
            <input
              type="email"
              name="user_email"
              id="email"
              value={userData?.user_email ?? user?.user_email ?? ''}
              onChange={handleInputChange}
              placeholder={user?.user_email ?? 'Please add your email'}
              autoComplete="email"
              className={`mt-1 block w-full rounded-md border ${
                emailError ? 'border-red-500' : 'border-gray-300'
              } px-3 py-2 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-sky-500 sm:text-sm`}
            />
            {emailError && (
              <p className="mt-1 text-xs text-red-500">{emailError}</p>
            )}
            {hasEmailChanged() && !emailError && (
              <p className="mt-1 text-xs text-blue-500">
                You will receive an OTP at this address to confirm the change.
              </p>
            )}
          </div>
        </div>

        <div className="mt-2 grid grid-cols-12 gap-6">
          <div className="col-span-12 w-64 sm:col-span-6">
            <label
              htmlFor="user_birthday"
              className="block text-sm font-medium text-gray-700"
            >
              Birthday
            </label>
            <input
              type="date"
              name="user_birthday"
              id="user_birthday"
              value={
                userData?.user_birthday
                  ? formatDate(userData?.user_birthday)
                  : ''
              }
              onChange={handleInputChange}
              max={(() => {
                const date = new Date();
                date.setFullYear(date.getFullYear() - 13);
                return date.toISOString().split('T')[0];
              })()}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-sky-500 sm:text-sm"
            />
          </div>
        </div>

        <div className="mt-6 flex w-full justify-end">
          <Button
            type="button"
            onClick={handleSubmit}
            className="min-w-24 disabled:bg-gray-700"
            disabled={
              !dataChanged ||
              isLoading ||
              !!emailError ||
              !!nameError ||
              !userData?.user_name ||
              !userData?.user_email
            }
          >
            {isLoading ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>

      {showConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">
              Confirm Changes
            </h3>
            <p className="mt-3 text-sm text-gray-600">
              Changing your {hasEmailChanged() ? 'email address' : 'name'} will{' '}
              <span className="font-semibold">
                remove all linked payment methods
              </span>
              . You will need to re-add them after the change.
            </p>
            {hasEmailChanged() && (
              <p className="mt-2 text-sm text-gray-600">
                An OTP will be sent to{' '}
                <span className="font-medium">{userData?.user_email}</span> to
                verify the new address.
              </p>
            )}
            <p className="mt-4 text-sm text-gray-600">
              Are you sure you want to proceed?
            </p>

            <div className="mt-6 flex gap-3">
              <Button
                type="button"
                className="flex-1 bg-[#5CB891] hover:bg-[#5CB891] rounded-full"
                onClick={handleConfirmedSave}
                disabled={isLoading}
              >
                {isLoading ? 'Loading...' : 'Yes, change'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="flex-1 bg-gray-100 text-gray-800 hover:bg-gray-200 rounded-full"
                onClick={() => setShowConfirmModal(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      <EmailOtpVerificationDialog
        open={emailOtpStep}
        pendingEmail={pendingEmail}
        isLoading={isOtpLoading}
        error={otpError}
        onVerify={(otp) => void handleOtpSubmit(otp)}
        onClose={() => {
          setEmailOtpStep(false);
          setOtpError(null);
        }}
      />
    </form>
  );
};
